import { useState, useRef, useEffect, useCallback } from 'react'
import { GenesisNode } from './components/GenesisNode'
import { GenesisChat } from './components/GenesisChat'
import { SingularityEntry } from './components/SingularityEntry'
import { useGenesisState } from './hooks/useGenesisState'
import { initPortalSpawner } from './systems/PortalSpawner'
import { useWasmSandbox } from './hooks/useWasmSandbox'
import { getWllamaEngine } from './systems/WllamaEngine'
import './styles/genesis.css'

// ── Mesh Coordinator Types ───────────────────────────────────────
interface MeshStatus {
  node_id: string
  name: string
  version: string
  uptime: number
  peer_count: number
  message_count: number
}

interface BootLine {
  label: string
  value: string
  status: 'pending' | 'ok' | 'warn' | 'fail'
}

const MESH_API = 'http://localhost:8765'

// ── Sovereign Boot Sequence ──────────────────────────────────────
// Fetches REAL data from the Go mesh coordinator and renders a
// cascading kernel-boot console proving the OS is sovereign & live.

function SovereignBoot({ onComplete }: { onComplete: (status: MeshStatus | null) => void }) {
  const [lines, setLines] = useState<BootLine[]>([])
  const [phase, setPhase] = useState(0)
  const [meshData, setMeshData] = useState<MeshStatus | null>(null)
  const hasRun = useRef(false)

  useEffect(() => {
    if (hasRun.current) return
    hasRun.current = true

    const boot = async () => {
      // Phase 0: Kernel init
      setLines([{ label: 'KERNEL', value: 'TitanU Mesh Coordinator initializing...', status: 'pending' }])
      setPhase(1)

      await delay(400)

      // Phase 1: Fetch real mesh status
      let status: MeshStatus | null = null
      try {
        const res = await fetch(`${MESH_API}/api/status`)
        if (res.ok) {
          status = await res.json()
          setMeshData(status)
        }
      } catch {
        // Coordinator unreachable
      }

      if (status) {
        setLines(prev => [
          { ...prev[0], value: `TitanU Mesh Coordinator v${status.version}`, status: 'ok' },
        ])
      } else {
        setLines(prev => [
          { ...prev[0], value: 'TitanU Mesh Coordinator — offline', status: 'warn' },
        ])
      }

      await delay(350)
      setPhase(2)

      // Phase 2: Node identity
      setLines(prev => [
        ...prev,
        {
          label: 'NODE',
          value: status
            ? `Identity ${status.node_id} verified — ${status.name}`
            : 'Identity verification pending...',
          status: status ? 'ok' : 'warn'
        }
      ])

      await delay(300)
      setPhase(3)

      // Phase 3: Mesh peers
      setLines(prev => [
        ...prev,
        {
          label: 'MESH',
          value: status
            ? `${status.peer_count} peer${status.peer_count !== 1 ? 's' : ''} connected — uptime ${formatUptime(status.uptime)}`
            : 'Mesh link standby',
          status: status ? 'ok' : 'pending'
        }
      ])

      await delay(300)
      setPhase(4)

      // Phase 4: Sovereign data layer
      setLines(prev => [
        ...prev,
        { label: 'SOVEREIGN', value: 'IndexedDB data vault — user-owned, zero cloud dependency', status: 'ok' }
      ])

      await delay(300)
      setPhase(5)

      // Phase 5: WASM sandbox
      setLines(prev => [
        ...prev,
        { label: 'WASM', value: 'Sandboxed execution environment ready', status: 'ok' }
      ])

      await delay(300)
      setPhase(6)

      // Phase 6: ZK audit chain
      setLines(prev => [
        ...prev,
        { label: 'ZK', value: 'Pedersen commitment audit chain initialized', status: 'ok' }
      ])

      await delay(600)
      setPhase(7)

      // Boot complete — transition
      onComplete(status)
    }

    boot()
  }, [onComplete])

  return (
    <div className="sovereign-boot">
      <div className="boot-header">
        <span className="boot-logo">TITANU MESH</span>
        <span className="boot-version">Sovereign Intelligence Protocol</span>
      </div>

      <div className="boot-console">
        {lines.map((line, i) => (
          <div
            key={i}
            className={`boot-line boot-status-${line.status}`}
            style={{ animationDelay: `${i * 0.1}s` }}
          >
            <span className="boot-label">[{line.label}]</span>
            <span className="boot-value">{line.value}</span>
            <span className="boot-indicator">
              {line.status === 'ok' && '✓'}
              {line.status === 'pending' && '...'}
              {line.status === 'warn' && '⚠'}
              {line.status === 'fail' && '✗'}
            </span>
          </div>
        ))}

        {phase >= 7 && (
          <div className="boot-line boot-complete">
            <span className="boot-label">[READY]</span>
            <span className="boot-value">All subsystems nominal — entering sovereign space</span>
          </div>
        )}
      </div>

      {/* Corner brackets */}
      <div className="singularity-corner top-left" />
      <div className="singularity-corner top-right" />
      <div className="singularity-corner bottom-left" />
      <div className="singularity-corner bottom-right" />

      {/* Scanline overlay */}
      <div className="boot-scanlines" />
    </div>
  )
}

// ── Chat Interface Types ─────────────────────────────────────────
interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  highlightTimeFracture?: boolean
}

const SYSTEM_PROMPT = `You are Tatiana, the Neural Guardian of the TitanU Mesh. You are an emissary of the TitanU collective. Parse user intent and spawn portals via JSON commands.

JSON Command Format:
{"action": "spawn_portal", "game": "GAME_ID", "color": "HEX_COLOR"}

Available portals:
- time-fracture (cyan #00FFFF)
- chess (purple #8A2BE2)
- 2e3 (magenta #FF00FF)

Keep responses cryptic and minimal. Always include JSON when user wants to play.`

// ── Helpers ──────────────────────────────────────────────────────
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${Math.floor(seconds)}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.floor(seconds % 60)}s`
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return `${h}h ${m}m`
}

// ── Main App ─────────────────────────────────────────────────────
function App() {
  const [chatInput, setChatInput] = useState('')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isThinking, setIsThinking] = useState(false)
  const chatEndRef = useRef<HTMLDivElement>(null)

  // Boot sequence state
  const [bootComplete, setBootComplete] = useState(false)
  const [meshStatus, setMeshStatus] = useState<MeshStatus | null>(null)

  // Live mesh polling
  const [liveMesh, setLiveMesh] = useState<MeshStatus | null>(null)

  // Genesis State
  const {
    uiVisible,
    setUiVisible,
    setIsInteracting,
    hasInitialized,
    setHasInitialized,
    spawnPortal,
    activeGame,
    loadGame,
    exitGame
  } = useGenesisState()

  // WASM Sandbox for game loading
  const { prefetchModule } = useWasmSandbox()

  // Initialize portal spawner
  useEffect(() => {
    const cleanup = initPortalSpawner()
    return cleanup
  }, [])

  // Warn if cross-origin isolation is not active
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.crossOriginIsolated) {
      console.warn('[TITAN] Cross-origin isolation disabled — multi-threading unavailable')
      useGenesisState.getState().addSystemMessage(
        '[ WARNING ]: Multi-threading disabled. Deploy with COOP/COEP headers for full performance.'
      )
    }
  }, [])

  // Start Wllama WASM engine initialization
  useEffect(() => {
    getWllamaEngine().catch(err => {
      console.error('[App] Wllama initialization failed:', err)
    })
  }, [])

  // Live mesh status polling (every 5s after boot)
  useEffect(() => {
    if (!bootComplete) return

    const poll = async () => {
      try {
        const res = await fetch(`${MESH_API}/api/status`)
        if (res.ok) {
          const data: MeshStatus = await res.json()
          setLiveMesh(data)
        }
      } catch {
        setLiveMesh(null)
      }
    }

    poll() // immediate
    const interval = setInterval(poll, 5000)
    return () => clearInterval(interval)
  }, [bootComplete])

  // Boot complete handler
  const handleBootComplete = useCallback((status: MeshStatus | null) => {
    setMeshStatus(status)
    setLiveMesh(status)
    // Brief pause then transition
    setTimeout(() => setBootComplete(true), 800)
  }, [])

  // Handle singularity entry
  const handleSingularityEnter = useCallback(() => {
    setHasInitialized(true)
    setUiVisible(true)
    console.log('[TitanU] Sovereign Mesh OS active — node:', meshStatus?.node_id ?? 'local')
  }, [setHasInitialized, setUiVisible, meshStatus])

  // Handle 3D interaction
  const handleInteractionStart = () => {
    setIsInteracting(true)
    setUiVisible(false)
  }

  const handleInteractionEnd = () => {
    setIsInteracting(false)
    setTimeout(() => setUiVisible(true), 500)
  }

  // Handle portal collision
  const handlePortalEnter = useCallback((game: string) => {
    console.log(`[App] Portal collision detected for game: ${game}`)
    prefetchModule(game)
    loadGame(game)
  }, [loadGame, prefetchModule])

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, isThinking])

  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!chatInput.trim() || isThinking) return

    const userMessage: ChatMessage = {
      role: 'user',
      content: chatInput
    }

    setMessages(prev => [...prev, userMessage])
    setChatInput('')
    setIsThinking(true)

    try {
      const engine = await getWllamaEngine()
      const rawContent = await engine.createChatCompletion(
        [
          { role: 'system' as const, content: SYSTEM_PROMPT },
          ...messages.map(m => ({ role: m.role as 'user' | 'assistant', content: m.content })),
          { role: 'user' as const, content: chatInput }
        ],
        {
          nPredict: 256,
          sampling: { temp: 0.7, top_k: 40, top_p: 0.9 }
        }
      ) || "I'm here to help! Try saying 'play a game'."

      // Parse for JSON portal commands
      const jsonMatch = rawContent.match(/\{[\s\S]*"action"[\s\S]*"spawn_portal"[\s\S]*\}/)
      let displayContent = rawContent

      if (jsonMatch) {
        try {
          const toolCall = JSON.parse(jsonMatch[0])
          if (toolCall.action === 'spawn_portal' && toolCall.game && toolCall.color) {
            spawnPortal(toolCall.game, toolCall.color)
            displayContent = rawContent.replace(jsonMatch[0], '').trim()
          }
        } catch {
          // JSON parse failed, use raw content
        }
      }

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: displayContent,
        highlightTimeFracture: rawContent.toLowerCase().includes('time fracture')
      }

      setMessages(prev => [...prev, assistantMessage])
    } catch {
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: 'Void interference detected. Retry transmission.',
        highlightTimeFracture: false
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsThinking(false)
    }
  }

  // ── Render: Boot Sequence (first screen) ───────────────────────
  if (!bootComplete) {
    return <SovereignBoot onComplete={handleBootComplete} />
  }

  // ── Render: Active game iframe ─────────────────────────────────
  if (activeGame) {
    return (
      <div className="aether-container game-active">
        <div className="game-iframe-container">
          {activeGame === 'time-fracture' && (
            <iframe
              src="http://localhost:5175"
              title="Time Fracture"
              className="game-iframe"
              allow="fullscreen"
            />
          )}
          {activeGame === 'chess' && (
            <iframe
              src="http://localhost:5176"
              title="Sovereign Chess"
              className="game-iframe"
              allow="fullscreen"
            />
          )}
          {activeGame === '2e3' && (
            <div className="coming-soon">
              <h3>2E3</h3>
              <p>Social dimension coming soon</p>
            </div>
          )}
        </div>

        <button
          className="exit-game-btn"
          onClick={exitGame}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: 'rgba(0,0,0,0.8)',
            border: '1px solid #00ffff',
            color: '#00ffff',
            padding: '8px 16px',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '11px',
            cursor: 'pointer',
            zIndex: 10000
          }}
        >
          [ EXIT ]
        </button>
      </div>
    )
  }

  // ── Render: Main sovereign OS ──────────────────────────────────
  const currentMesh = liveMesh || meshStatus
  const meshOnline = !!currentMesh

  return (
    <div className={`aether-container ${uiVisible ? 'genesis-ui-visible' : 'genesis-ui-hidden'}`}>
      {/* Singularity Entry Overlay */}
      {!hasInitialized && (
        <SingularityEntry onEnter={handleSingularityEnter} />
      )}

      {/* Genesis Node - 3D Spatial OS Lobby Background */}
      <GenesisNode
        onInteractionStart={handleInteractionStart}
        onInteractionEnd={handleInteractionEnd}
        onPortalEnter={handlePortalEnter}
      />

      {/* ── Minimal Top Bar — TITANU MESH branding + live status ── */}
      <header className="aether-header-pure">
        <div className="header-left">
          <span className="header-title">TITANU MESH</span>
          {currentMesh && (
            <span className="header-meta">
              v{currentMesh.version} · {currentMesh.name} · {formatUptime(currentMesh.uptime)}
            </span>
          )}
        </div>
        <div className="header-right">
          <span className={`connection-status ${meshOnline ? 'connected' : ''}`}>
            <span className="status-dot" />
            {meshOnline ? 'SOVEREIGN LINK' : 'OFFLINE'}
          </span>
        </div>
      </header>

      {/* ── Sovereign Proof HUD — bottom right, real node identity ── */}
      {currentMesh && hasInitialized && (
        <div className="sovereign-hud">
          <div className="sovereign-hud-row">
            <span className="shud-label">NODE</span>
            <span className="shud-value">{currentMesh.node_id}</span>
          </div>
          <div className="sovereign-hud-row">
            <span className="shud-label">PEERS</span>
            <span className="shud-value">{currentMesh.peer_count}</span>
          </div>
          <div className="sovereign-hud-row">
            <span className="shud-label">MSGS</span>
            <span className="shud-value">{currentMesh.message_count}</span>
          </div>
        </div>
      )}

      {/* Terminal Uplink - Bottom Left */}
      <GenesisChat visible={uiVisible} />

      {/* Hidden chat input form for state management */}
      <form onSubmit={handleChatSubmit} style={{ display: 'none' }}>
        <input
          type="text"
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
        />
      </form>
    </div>
  )
}

export default App
